import { useState, useMemo } from 'react';
import type { MouseEventHandler } from 'react';
// components
import type { IAct } from '@newo/api/gateway';
import type { TimelineItem } from './ActsHistory.utils.ts';
import { buildTimeline } from './ActsHistory.utils.ts';
import AgentMessage from '@/modules/Chat/components/AgentMessage';
import UserMessage from '@/modules/Chat/components/UserMessage';
import ConversationsEvent from '@/modules/Chat/components/ConversationsEvent';
// utils
import useActLogDrawerStore from '@/modules/ActLogDrawer/ActLogDrawer.store.ts';
import useActReportDrawerStore from '@/modules/ActReportDrawer/ActReportDrawer.store.ts';
import DataDivider from '@/modules/Chat/components/DataDivider';
import useSettingsStore from '@/store/settings.ts';

interface ActsHistoryProps {
    acts: IAct[];
}

const ActsHistory = ({ acts }: ActsHistoryProps) => {
    const [selectedAct, setSelectedAct] = useState<IAct>();
    const setAct = useActLogDrawerStore((state) => state.setAct);
    const selectActToReport = useActReportDrawerStore(
        (state) => state.selectActToReport
    );
    const isUTC = useSettingsStore((state) => state.settings.isUTC);
    const timeZone = useSettingsStore((state) => state.settings.timeZone);
    const handleSelected = (value: IAct) => {
        setSelectedAct(value);
    };

    const actsTimeline = useMemo(
        () => buildTimeline(acts, isUTC, timeZone, '2m'),
        [acts]
    );

    return actsTimeline.map((elm: TimelineItem) => {
        if (elm.type === 'separator') {
            return <DataDivider date={elm.date} key={`sep-${elm.date}`} />;
        }

        const handleDetailsClick: MouseEventHandler<HTMLButtonElement> = (
            event_
        ) => {
            event_.stopPropagation();
            setAct(elm.act);
        };

        const handleReportClick: MouseEventHandler<HTMLButtonElement> = (
            event_
        ) => {
            event_.stopPropagation();
            selectActToReport(elm.act);
        };

        switch (elm.act.reference_idn) {
            case 'agent_message': {
                return (
                    <AgentMessage
                        className="mb-3 mt-3"
                        connectorIdn={elm.act.connector_idn}
                        content={elm.act.original_text}
                        datetime={elm.act.datetime}
                        integrationIdn={elm.act.integration_idn}
                        key={elm.act.id}
                        onDetails={handleDetailsClick}
                        onReport={handleReportClick}
                    />
                );
            }
            case 'user_message': {
                return (
                    <UserMessage
                        className="mb-3 mt-3"
                        connectorIdn={elm.act.connector_idn}
                        content={elm.act.original_text}
                        datetime={elm.act.datetime}
                        externalId={elm.act.external_id}
                        integrationIdn={elm.act.integration_idn}
                        key={elm.act.id}
                    />
                );
            }
            default: {
                return (
                    <ConversationsEvent
                        act={elm.act}
                        handleSelected={() => {
                            handleSelected(elm.act);
                        }}
                        key={elm.act.id}
                        selectedAct={selectedAct}
                        onDetails={handleDetailsClick}
                        onReport={handleReportClick}
                    />
                );
            }
        }
    });
};

ActsHistory.displayName = 'ActsHistory';

export default ActsHistory;
